import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AppLogo } from "@/components/AppLogo";
import { Building2, Search } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { toast } from "sonner";

import { authErrorMessage, safeAuth } from "@/lib/auth-errors";

export const Route = createFileRoute("/register")({
  head: () => ({
    meta: [
      { title: "Join Landoo" },
      {
        name: "description",
        content: "Create your Landoo account as a property seeker or partner.",
      },
      { property: "og:title", content: "Join Landoo" },
      {
        property: "og:description",
        content: "Create your Landoo account as a property seeker or partner.",
      },
    ],
  }),
  component: RegisterPage,
});

function RegisterPage() {
  const nav = useNavigate();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    let data, error;
    try {
      ({ data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin,
          data: { full_name: fullName, role: "customer" },
        },
      }));
    } catch (err) {
      setSubmitting(false);
      return toast.error(authErrorMessage(err));
    }
    setSubmitting(false);
    if (error) return toast.error(authErrorMessage(error));
    if (!data) return toast.error("Something went wrong. Try again.");
    if (!data.session) {
      toast.success("Check your email to confirm your account.");
      nav({ to: "/login" });
      return;
    }
    toast.success("Welcome to Landoo!");
    nav({ to: "/customer/dashboard", replace: true });
  };

  const onGoogle = async () => {
    const { error } = await safeAuth(
      () =>
        lovable.auth.signInWithOAuth("google", {
          redirect_uri: window.location.origin,
        }) as Promise<{ error?: unknown }>,
      "Google sign-in failed",
    );
    if (error) toast.error(error);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream/40 p-4">
      <div className="w-full max-w-2xl rounded-3xl border border-border bg-card p-6 shadow-float">
        <div className="mb-6">
          <AppLogo />
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <h1 className="text-xl font-bold text-brown-dark">Create your account</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Sign up as a customer to explore properties and save favourites.
            </p>
            <form className="mt-5 space-y-3" onSubmit={onSubmit}>
              <input
                required
                placeholder="Full name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30"
              />
              <input
                type="email"
                required
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30"
              />
              <input
                type="password"
                required
                minLength={6}
                placeholder="Password (min 6 chars)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30"
              />
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-xl bg-coral py-2.5 text-sm font-semibold text-white shadow-soft transition hover:brightness-105 disabled:opacity-50"
              >
                {submitting ? "Creating account…" : "Create customer account"}
              </button>
              <button
                type="button"
                onClick={onGoogle}
                className="w-full rounded-xl border border-border bg-background py-2.5 text-sm font-semibold text-brown-dark shadow-soft transition hover:bg-brand-soft"
              >
                Continue with Google
              </button>
            </form>
          </div>

          <div className="flex flex-col gap-3 rounded-2xl border border-border bg-cream/40 p-4">
            <div className="flex items-center gap-2 text-brown-dark">
              <Search className="h-5 w-5" />{" "}
              <span className="font-semibold">Looking for property?</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Customer accounts can browse the map, save favourites and unlock partner contacts.
            </p>
            <div className="my-2 h-px w-full bg-border" />
            <div className="flex items-center gap-2 text-brown-dark">
              <Building2 className="h-5 w-5" />{" "}
              <span className="font-semibold">Listing property?</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Register as a partner to list houses, land or commercial spaces.
            </p>
            <Link
              to="/partner-register"
              className="mt-1 rounded-xl bg-brown px-3 py-2 text-center text-xs font-semibold text-primary-foreground"
            >
              Register as partner
            </Link>
          </div>
        </div>
        <div className="mt-6 text-xs text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-brown-dark hover:underline">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
